import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Aktiver Patient-Keypair-Eintrag für den Polling-Loop.
 *
 * Lebt nicht im Reducer-State, weil `CryptoKey` nicht JSON-serialisierbar ist
 * und sonst React-DevTools, Logger oder Telemetrie ihn versehentlich
 * weiterreichen könnten. Stattdessen: dedizierter Context, der nur den
 * Polling-Hook (`usePollStatus`) und seinen Submit/Recovery-Setter sieht.
 */
interface PatientKeypairEntry {
  readonly tenantId: string;
  readonly queueItemId: string;
  readonly privateKey: CryptoKey;
}

interface PatientKeypairContextValue {
  readonly entry: PatientKeypairEntry | null;
  readonly setEntry: (entry: PatientKeypairEntry | null) => void;
}

const DEFAULT_VALUE: PatientKeypairContextValue = {
  entry: null,
  setEntry: () => undefined,
};

const PatientKeypairContext = createContext<PatientKeypairContextValue>(DEFAULT_VALUE);

interface PatientKeypairProviderProps {
  readonly children: ReactNode;
  /** Optionaler initialer Eintrag — z. B. aus dem Recovery-Bootstrap. */
  readonly initial?: PatientKeypairEntry | null;
}

/**
 * Provider, der den Keypair-Eintrag im Subtree hält. Setter wird via Context
 * exponiert, damit `useCancelSubmit` und der Recovery-Pfad denselben Slot
 * füllen können.
 */
export function PatientKeypairProvider(props: PatientKeypairProviderProps): JSX.Element {
  const [entry, setEntry] = useState<PatientKeypairEntry | null>(props.initial ?? null);
  const value = useMemo<PatientKeypairContextValue>(() => ({ entry, setEntry }), [entry]);
  return (
    <PatientKeypairContext.Provider value={value}>{props.children}</PatientKeypairContext.Provider>
  );
}

/**
 * Lese-/Schreib-Hook für den Keypair-Eintrag. Konsumenten innerhalb des
 * Providers können den Eintrag lesen oder ersetzen (z. B. bei Submit-Erfolg).
 */
export function usePatientKeypair(): PatientKeypairContextValue {
  return useContext(PatientKeypairContext);
}
